import { useQueryClient } from "@tanstack/react-query"
import {
  ArrowLeftIcon,
  FileTextIcon,
  InfoIcon,
  OctagonAlertIcon,
  ReceiptTextIcon,
  Share2Icon,
  TriangleAlertIcon,
} from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useParams } from "react-router-dom"
import { toast } from "sonner"
import { FeatureOff } from "@/components/FeatureOff"
import { SyncStatus } from "@/components/SyncStatus"
import { DocumentStatusBadge } from "@/features/invoices/DocumentStatusBadge"
import { LineRow, PdfPanel } from "@/features/invoices/InvoiceDetailScreen"
import {
  useCancelCreditNote,
  useCreditNote,
  useCreditNotePdf,
} from "@/features/credit-notes/use-credit-note"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/features/auth/auth-context"
import { ApiError } from "@/lib/api-error"
import { requestBlob } from "@/lib/api"
import { useFormatDay } from "@/lib/dates"
import { endpoints, type CreditNoteDetail } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { shareDocument } from "@/lib/share"
import { routes } from "@/routes/paths"

/**
 * One credit note (§17.3, section 34(1) CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT IS A TAX DOCUMENT, AND IT LOOKS LIKE THE BILL IT REDUCES. Same header, same
 * seven-column line table (the invoice's own `LineRow`), same totals shape, same
 * print-and-share row, same lone destructive card at the foot. The owner asked
 * twice that the documents look the same except where they genuinely differ, and
 * this one differs in exactly four places, each argued below: it NAMES an
 * invoice, it carries a REASON, its total is captioned "Total credit" rather
 * than "Grand total", and it can never be edited.
 *
 * THERE IS NO EDIT CONTROL, NOT EVEN A DISABLED ONE. `creditnote.edit` does not
 * exist as a capability for anyone (§2.1), exactly like `invoice.edit`, and
 * `isEditable` is always false on the wire. A wrong credit note is CANCELLED and
 * a fresh one raised — which is what §34 expects anyway, since the wrong one has
 * already been declared in a return.
 *
 * NOTHING PRINTS A MINUS SIGN. Every amount is positive (§17.3); the document
 * type carries the meaning "this reduces", and the totals card says so in words.
 * The one signed figure is `roundOffPaise`, which is a rounding rather than an
 * amount and is signed on every document in this system.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function CreditNoteDetailScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()
  const formatDay = useFormatDay()
  const location = useLocation()
  const queryClient = useQueryClient()

  const query = useCreditNote(id)
  const note = query.data
  const pdf = useCreditNotePdf(id, note?.number)

  /*
   * §34(2)'s DEADLINE, HANDED OVER BY THE EDITOR THROUGH THE ROUTER.
   *
   * `POST /credit-notes` answers 201 with a `warnings` array, and the sentence
   * that matters says the 30 November following the end of the financial year of
   * the SUPPLY has passed, so the tax adjustment is lost. That is not a failure —
   * the note exists and holds its number — but it must not be a toast on a
   * screen that is being replaced, which nobody reads. It rides in
   * `location.state` and is rendered here, once, on the arrival.
   *
   * NOTHING RE-DERIVES THE DATE. The rule is Asia/Kolkata, keyed to the
   * financial year of the ORIGINAL supply rather than of the note, and it is the
   * server's. A second implementation here would be a second answer.
   */
  const warnings = readWarnings(location.state)

  const [sharing, setSharing] = useState(false)
  const onShare = async () => {
    if (note === undefined || id === undefined) return
    setSharing(true)
    try {
      const filename = `${note.number.replace(/\//g, "-")}.pdf`
      const fetched = await requestBlob(endpoints.creditNotePdf(id), {
        fallbackFilename: filename,
      })
      const outcome = await shareDocument({
        title: t("share.titleCreditNote", { number: note.number }),
        text: t("share.messageCreditNote", {
          number: note.number,
          invoice: note.originalInvoiceNumber,
          amount: formatRupees(note.totals.grandTotalPaise),
        }),
        file: { blob: fetched.blob, filename: fetched.filename ?? filename },
        phone: note.customer?.phone ?? null,
      })
      if (outcome.status === "fallback") toast.info(t("share.openedWhatsapp"))
      if (outcome.status === "failed") toast.error(t("share.noShareTarget"))
    } catch {
      toast.error(t("share.couldNotPrepare"))
    } finally {
      setSharing(false)
    }
  }

  if (query.isPending) return <DetailSkeleton />

  if (query.isError || note === undefined) {
    /*
     * §18.3 — A 404 HERE IS AMBIGUOUS AND MUST STAY THAT WAY. Every credit-note
     * route sits behind `requireFlag("credit_notes")`, which runs before
     * `requireCap` and answers 404 rather than 403 so that "forbidden" cannot
     * leak the existence of unreleased work. THE FLAG SHIPS DISABLED, so "the
     * module is off" is the ordinary meaning today — which is why this branch
     * says "not switched on" and offers no retry. Pressing a button cannot flip
     * a flag, and one that never works is how someone concludes the app is
     * broken rather than merely incomplete. It must never say "you do not have
     * permission", which is precisely the leak the 404 was chosen to avoid.
     */
    const notFound = query.error instanceof ApiError && query.error.status === 404

    /*
     * ONE COMPONENT AND ONE PAIR OF STRINGS FOR EVERY FLAGGED MODULE —
     * `components/FeatureOff.tsx`, which replaced this screen's own
     * `creditNote.notAvailableTitle`/`Help`. The module is named from the NAV
     * LABEL here, because credit notes do have a sidebar row and the two must
     * read as the same thing.
     *
     * The genuine-failure branch below is untouched: it keeps its retry, because
     * a request that failed CAN succeed on a second press, while a flag cannot
     * be flipped by a button.
     */
    if (notFound) {
      return (
        <div className="flex flex-col gap-4 p-4 md:p-6">
          <BackLink />
          <FeatureOff module={t("nav.creditNotes")} icon={<InfoIcon />} />
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <BackLink />
        <Alert variant="destructive">
          <InfoIcon />
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError
                ? query.error.message
                : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const cancelled = note.status === "CANCELLED"

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight tabular-nums">{note.number}</h1>
            <DocumentStatusBadge status={note.status} kind="creditNote" />
          </div>
          <p className="text-muted-foreground text-sm">
            {/* The FROZEN snapshot, copied off the invoice (§8.7) — the buyer as
              * they were billed, not as the customer row stands today. */}
            {note.customer?.name ?? t("lbl.customerUnknown")} · {formatDay(note.date)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SyncStatus
            updatedAt={query.dataUpdatedAt}
            syncing={query.isFetching}
            onSync={() => {
              void query.refetch()
            }}
          />

          {/*
            * ONE PRINT BUTTON AND NO COPIES MENU. Rule 55(2)'s three captioned
            * copies are a CHALLAN rule and an invoice has a `copy_type` column;
            * `credit_notes` has neither, and the route takes no `?copy=`. A menu
            * offering "Duplicate" would print the identical sheet under a name
            * that claims a distinction the document does not have.
            */}
          <Button
            onClick={() => {
              pdf.mutate()
            }}
            disabled={pdf.isPending}
          >
            <FileTextIcon />
            {pdf.isPending ? t("msg.loading") : t("creditNote.viewAction")}
          </Button>

          <Button
            variant="outline"
            disabled={sharing}
            onClick={() => {
              void onShare()
            }}
          >
            <Share2Icon />
            {sharing ? t("share.preparing") : t("btn.share")}
          </Button>
        </div>
      </header>

      {/*
        * THE §34(2) SENTENCE, IF THE SERVER SENT ONE. Amber rather than red: the
        * note WAS issued and holds its number. What may be lost is the tax
        * adjustment, and the server's own words say which date passed.
        */}
      {warnings.map((warning) => (
        <Alert key={warning}>
          <TriangleAlertIcon />
          <AlertTitle>{t("creditNote.deadlineTitle")}</AlertTitle>
          <AlertDescription>{warning}</AlertDescription>
        </Alert>
      ))}

      {cancelled ? (
        /*
         * A CANCELLED CREDIT NOTE CREDITS NOTHING, and that has a consequence
         * the owner has to be told rather than left to infer: the money went
         * BACK onto the bill. Every sum over `credit_notes` counts ISSUED rows
         * only, so cancelling one raises what the customer owes on the invoice,
         * the unpaid register, the statement and the home screen at once.
         */
        <Alert variant="destructive">
          <TriangleAlertIcon />
          <AlertTitle>{t("creditNote.cancelledTitle", { number: note.number })}</AlertTitle>
          <AlertDescription>
            {t("creditNote.cancelledHelp", { number: note.originalInvoiceNumber })}
            {note.cancelReason === null ? null : (
              <span className="mt-2 block">
                {t("doc.cancelledReason", { reason: note.cancelReason })}
              </span>
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <Alert>
          <InfoIcon />
          <AlertTitle>{t("creditNote.standsTitle")}</AlertTitle>
          <AlertDescription>
            {t("creditNote.standsHelp", {
              invoice: note.originalInvoiceNumber,
              amount: formatRupees(note.totals.grandTotalPaise),
            })}
          </AlertDescription>
        </Alert>
      )}

      {pdf.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("doc.pdfFailedTitle")}</AlertTitle>
          <AlertDescription>
            {pdf.error instanceof ApiError ? pdf.error.message : t("doc.pdfFailedHelp")}
          </AlertDescription>
        </Alert>
      ) : null}

      {pdf.url === null ? null : (
        <PdfPanel url={pdf.url} filename={pdf.filename} titleKey="creditNote.pdfTitle" />
      )}

      <CreditNoteFacts note={note} />

      <Card className="overflow-hidden py-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>{t("lbl.description")}</TableHead>
                {/* "HSN" is the statutory acronym and prints as HSN on the sheet
                  * in both languages, so it is not translated. */}
                <TableHead>HSN</TableHead>
                <TableHead className="text-right">{t("lbl.quantity")}</TableHead>
                <TableHead className="text-right">{t("lbl.rate")}</TableHead>
                <TableHead className="text-right">{t("lbl.taxRate")}</TableHead>
                <TableHead className="text-right">{t("lbl.amount")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {note.lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                    {t("doc.noLines")}
                  </TableCell>
                </TableRow>
              ) : (
                /* THE INVOICE'S OWN ROW COMPONENT — the rates on these lines are
                 * the ORIGINAL supply's snapshot (§8.7), so a bill taxed at 12%
                 * is reversed at 12% however the Council has since moved it. */
                note.lines.map((line, index) => (
                  <LineRow key={line.id} line={line} index={index} />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <CreditTotalsCard note={note} />

      {note.notes === null ? null : (
        <Card>
          <CardHeader>
            <CardTitle>{t("lbl.notes")}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <p className="whitespace-pre-wrap">{note.notes}</p>
          </CardContent>
        </Card>
      )}

      {/*
        * The one destructive act, last and alone — the same placement the bill
        * uses for its cancellation, and gated on `creditnote.cancel`.
        */}
      {can("creditnote.cancel") && !cancelled ? (
        <CancelCreditNoteSection
          note={note}
          onCancelled={() => {
            /*
             * EVERYTHING, and here that is not caution — it is arithmetic.
             * Cancelling this note puts its amount back on what the customer
             * owes, which moves the bill's payment card, the unpaid register,
             * the per-customer statement and the home-screen figure. None of
             * those is under the `creditNotes` cache prefix.
             */
            void queryClient.invalidateQueries()
          }}
        />
      ) : null}
    </div>
  )
}

/** The router's `state` is `unknown`; read the one key we put there, safely. */
function readWarnings(state: unknown): string[] {
  if (typeof state !== "object" || state === null) return []
  const value = (state as { warnings?: unknown }).warnings
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === "string")
}

/**
 * §34's LINK AND §34(1)'s REASON — the two facts that make this a credit note
 * rather than an odd little invoice.
 *
 * THE ORIGINAL'S NUMBER IS A REAL HYPERLINK, and it is the only one here,
 * because `invoiceId` is a genuine foreign key that the API refuses to write
 * without. The number and date beside it are the FROZEN copies that print on the
 * sheet; the link uses the id, so a bill whose number was later corrected still
 * opens.
 */
function CreditNoteFacts({ note }: { note: CreditNoteDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const customer = note.customer

  const facts: { label: string; value: string | null; to?: string }[] = [
    {
      label: t("creditNote.originalNumberLabel"),
      value: note.originalInvoiceNumber,
      to: routes.invoice(note.invoiceId),
    },
    { label: t("creditNote.originalDateLabel"), value: formatDay(note.originalInvoiceDate) },
    { label: t("lbl.gstin"), value: customer?.gstin ?? null },
    {
      label: t("doc.placeOfSupply"),
      value: `${note.placeOfSupply} · ${customer?.stateName ?? ""}`.trim(),
    },
    {
      label: t("doc.taxType"),
      /*
       * COPIED FROM THE ORIGINAL INVOICE, never re-derived from the buyer's
       * state today (§8.7). A customer who has moved state since must not flip
       * an intra-state bill's CGST+SGST into IGST on the note — the original
       * liability would stay un-reversed in two heads while a new one was
       * created in a third.
       */
      value: note.supplyType === "INTER_STATE" ? t("doc.taxInter") : t("doc.taxIntra"),
    },
    { label: t("lbl.contactName"), value: note.attnName },
    { label: t("lbl.vendorCode"), value: customer?.vendorCode ?? null },
  ].filter((fact) => fact.value !== null && fact.value !== "")

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("doc.factsTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {customer === null ? null : (
          <address className="text-sm not-italic">
            <div className="font-medium">
              <Link
                to={routes.customer(note.customerId)}
                className="hover:text-primary underline-offset-4 hover:underline"
              >
                {customer.name}
              </Link>
            </div>
            {[customer.addressLine1, customer.addressLine2, customer.city, customer.pincode]
              .filter((part) => part !== null && part !== "")
              .map((part, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: address lines have no id.
                <div key={index} className="text-muted-foreground">
                  {part}
                </div>
              ))}
          </address>
        )}

        {/*
          * §34(1)'s REASON, GIVEN ITS OWN BLOCK RATHER THAN A ROW IN THE GRID.
          * It is a sentence, not a reference number — it wraps, and squeezing it
          * into a two-column definition list would truncate the one field an
          * assessing officer reads. Mandatory on every note, so there is no
          * "absent" case to handle.
          */}
        <div className="rounded-md border p-3 text-sm">
          <div className="text-muted-foreground text-xs">{t("creditNote.reasonLabel")}</div>
          <p className="whitespace-pre-wrap">{note.reason}</p>
        </div>

        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {facts.map((fact) => (
            <div key={fact.label} className="flex justify-between gap-4 border-b pb-1">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className="text-right font-medium">
                {fact.to === undefined ? (
                  fact.value
                ) : (
                  <Link
                    to={fact.to}
                    className="hover:text-primary inline-flex items-center gap-1 underline-offset-4 hover:underline"
                  >
                    <ReceiptTextIcon aria-hidden="true" className="size-3.5" />
                    {fact.value}
                  </Link>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}

/**
 * The totals, worded as a CREDIT.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * "TOTAL CREDIT", NOT "GRAND TOTAL" — the same wording the printed sheet uses
 * (`services/pdf/credit-note-pdf.ts`), and for the same reason: nobody is being
 * asked to pay this. A figure captioned "Grand total" on a document sitting in a
 * customer's file reads as a demand, and this one is the opposite.
 *
 * EVERY FIGURE IS POSITIVE and none carries a minus sign (§17.3). The direction
 * lives in the caption above the total, which says in words that the bill is
 * reduced by this much — because the missing minus is only safe if something
 * else says which way the money goes.
 *
 * The tax rows keep their statutory names — CGST, SGST, IGST — because that is
 * what is being reversed and what the accountant reconciles against GSTR-1's
 * CDNR table. The SPLIT follows `note.supplyType`, copied from the original.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function CreditTotalsCard({ note }: { note: CreditNoteDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const { totals } = note
  const interState = note.supplyType === "INTER_STATE"

  const rows: { label: string; paise: number; show: boolean }[] = [
    { label: t("lbl.subtotal"), paise: totals.subtotalPaise, show: true },
    { label: "CGST", paise: totals.cgstPaise, show: !interState },
    { label: "SGST", paise: totals.sgstPaise, show: !interState },
    { label: "IGST", paise: totals.igstPaise, show: interState },
    /* The ONE signed figure on any document in this system, and a rounding
     * rather than an amount — which is why the no-negatives rule does not
     * reach it. */
    { label: t("lbl.roundOff"), paise: totals.roundOffPaise, show: totals.roundOffPaise !== 0 },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("doc.totalsTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <dl className="ml-auto flex w-full max-w-sm flex-col gap-2 text-sm">
          {rows
            .filter((row) => row.show)
            .map((row) => (
              <div key={row.label} className="flex justify-between gap-6">
                <dt className="text-muted-foreground">{row.label}</dt>
                <dd className="tabular-nums">{formatRupees(row.paise)}</dd>
              </div>
            ))}
          <Separator />
          <div className="flex justify-between gap-6 text-base font-semibold">
            <dt>{t("creditNote.totalCredit")}</dt>
            <dd className="tabular-nums">{formatRupees(totals.grandTotalPaise)}</dd>
          </div>
        </dl>

        <p className="text-muted-foreground ml-auto w-full max-w-sm text-xs">
          {t("creditNote.reducesBillBy", { number: note.originalInvoiceNumber })}
        </p>

        {totals.amountInWords === "" ? null : (
          <div className="mt-2 border-t pt-3 text-sm">
            <span className="text-muted-foreground">{t("doc.amountInWords")}: </span>
            {/* Worded by the SERVER, so this page and the printed sheet cannot
              * word the same number differently. */}
            <span className="font-medium">{totals.amountInWords}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Cancelling, last and alone.
 *
 * IT PUTS THE MONEY BACK, AND THE DIALOG SAYS SO BEFORE THE BUTTON. Every sum
 * over `credit_notes` counts ISSUED rows only, so this raises what the customer
 * owes by exactly this amount — on the bill, the unpaid register, the statement
 * and the home screen. That is the correct meaning (a cancelled credit note
 * credits nothing) and it is the opposite of what "cancel" suggests to someone
 * who has just been chasing a payment, so it is spelled out rather than left to
 * be discovered on the next screen.
 *
 * THE NUMBER STAYS CONSUMED. §34(1) requires a consecutive series, so a missing
 * `CRN/26-27/2` is a question from the department — the same reasoning that
 * makes an invoice cancellable but never deletable (§5).
 *
 * THE REASON IS MANDATORY and the button stays disabled without it: `audit_log`
 * is an ADMIN surface (`audit.view`, §2.1) the owner cannot open, so if he is
 * ever to be told why the note in front of him credits nothing, it has to be on
 * the note.
 */
function CancelCreditNoteSection({
  note,
  onCancelled,
}: {
  note: CreditNoteDetail
  onCancelled: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState("")
  const cancel = useCancelCreditNote(String(note.id))

  // Stricter than the API (`.min(1)`), matching the invoice-cancel dialog. Do
  // not write this up as the server's rule: it is this screen being careful.
  const tooShort = reason.trim().length < 3

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive flex items-center gap-2 text-base">
          <OctagonAlertIcon aria-hidden="true" className="size-4 shrink-0" />
          {t("cancelCreditNote.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-3">
        <p className="text-muted-foreground text-sm">{t("cancelCreditNote.help")}</p>

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger
            render={
              <Button
                variant="outline"
                className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
              />
            }
          >
            <OctagonAlertIcon />
            {t("cancelCreditNote.action")}
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("cancelCreditNote.confirmTitle", { number: note.number })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("cancelCreditNote.confirmHelp")}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <ul className="text-muted-foreground flex list-disc flex-col gap-1.5 pl-5 text-sm">
              {/* THE CONSEQUENCE FIRST — the money goes back on the bill. */}
              <li>
                {t("cancelCreditNote.putsMoneyBack", {
                  amount: formatRupees(note.totals.grandTotalPaise),
                  number: note.originalInvoiceNumber,
                })}
              </li>
              <li>{t("cancelCreditNote.keepsNumber", { number: note.number })}</li>
              <li>{t("cancelCreditNote.cannotUndo")}</li>
            </ul>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cancel-credit-note-reason">
                {t("cancelCreditNote.reasonLabel")}
              </Label>
              <Textarea
                id="cancel-credit-note-reason"
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
                maxLength={500}
                rows={3}
              />
              <p className="text-muted-foreground text-xs">{t("cancelCreditNote.reasonHelp")}</p>
            </div>

            {cancel.isError ? (
              <Alert variant="destructive">
                <AlertDescription>
                  {cancel.error instanceof ApiError
                    ? cancel.error.message
                    : t("err.somethingWentWrong")}
                </AlertDescription>
              </Alert>
            ) : null}

            <AlertDialogFooter>
              <AlertDialogCancel render={<Button variant="outline" />}>
                {t("cancelCreditNote.keepIt")}
              </AlertDialogCancel>
              <Button
                variant="destructive"
                disabled={tooShort || cancel.isPending}
                onClick={() => {
                  cancel.mutate(reason.trim(), {
                    onSuccess: () => {
                      setOpen(false)
                      toast.success(t("cancelCreditNote.done", { number: note.number }))
                      onCancelled()
                    },
                  })
                }}
              >
                {t("cancelCreditNote.confirmAction")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}

function BackLink(): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <Link
      to={routes.creditNotes}
      className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
    >
      <ArrowLeftIcon aria-hidden="true" className="size-4" />
      {t("btn.back")}
    </Link>
  )
}

function DetailSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  )
}
